import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

const STATUS_OPTIONS = [
  { key: "solo", title: "Célibataire", desc: "Pour me préparer et m'auto-évaluer", account_type: "solo", stage: "single" },
  { key: "couple", title: "En relation / En couple", desc: "Pour évaluer notre compatibilité", account_type: "couple", stage: "considering" },
  { key: "engaged", title: "Fiancé(e)", desc: "Pour notre préparation au mariage", account_type: "couple", stage: "engaged" },
  { key: "married", title: "Marié(e)", desc: "Pour faire un bilan de notre couple", account_type: "couple", stage: "married" },
  { key: "pastor", title: "Pasteur / Conseiller", desc: "Pour accompagner des couples", account_type: "pastor", stage: null },
];

export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [statusKey, setStatusKey] = useState("engaged");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        const opt = STATUS_OPTIONS.find((o) => o.key === statusKey) || STATUS_OPTIONS[2];
        await register(name, email, password, opt.account_type, opt.stage);
        toast.success("Bienvenue dans votre parcours.");
      } else {
        await login(email, password);
        toast.success("Heureux de vous revoir.");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-100px)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1513267000941-598abe7be16f?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Mains entrelacées"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C2A29]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-4xl italic leading-tight max-w-md">
            « L'amour est patient, l'amour est plein de bonté. »
          </p>
          <p className="mt-3 text-xs tracking-[0.25em] uppercase opacity-80">
            1 Corinthiens 13:4
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-4">
            {mode === "register" ? "Commençons" : "Bon retour"}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight mb-3">
            {mode === "register" ? "Créez votre compte" : "Connectez-vous"}
          </h1>
          <p className="text-[#5C5754] mb-10">
            {mode === "register"
              ? "En couple, fiancé(e) ou célibataire : créez votre compte pour démarrer votre parcours."
              : "Reprenez votre parcours là où vous l'aviez laissé."}
          </p>
          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
                  Je suis
                </label>
                <div className="mt-3 space-y-2">
                  {STATUS_OPTIONS.map((o) => {
                    const active = statusKey === o.key;
                    return (
                      <button
                        key={o.key}
                        type="button"
                        data-testid={`status-option-${o.key}`}
                        onClick={() => setStatusKey(o.key)}
                        className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                          active
                            ? "border-[#C86B5E] bg-[#C86B5E]/5"
                            : "border-[#E6E2DD] bg-white hover:border-[#C86B5E]/40"
                        }`}
                      >
                        <span
                          className={`flex-shrink-0 w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                            active ? "border-[#C86B5E] bg-[#C86B5E]" : "border-[#C9C2BA] bg-white"
                          }`}
                        >
                          {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                        </span>
                        <span className="min-w-0">
                          <span className={`block text-sm font-medium leading-tight ${active ? "text-[#2C2A29]" : "text-[#2C2A29]"}`}>
                            {o.title}
                          </span>
                          <span className="block text-xs text-[#8A8480] leading-tight mt-0.5">
                            {o.desc}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {mode === "register" && (
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
                  Prénom
                </label>
                <input
                  data-testid="auth-name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3.5 text-[#2C2A29] transition-colors"
                  placeholder="Ton prénom"
                />
              </div>
            )}
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
                Email
              </label>
              <input
                data-testid="auth-email-input"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3.5 text-[#2C2A29] transition-colors"
                placeholder="tonemail@exemple.com"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
                Mot de passe
              </label>
              <input
                data-testid="auth-password-input"
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3.5 text-[#2C2A29] transition-colors"
                placeholder="••••••••"
                minLength={6}
              />
              {mode === "login" && (
                <div className="mt-2 text-right">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs text-[#C86B5E] hover:text-[#B35A4E]"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
              )}
            </div>
            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-50 text-white rounded-full px-8 py-4 transition-colors mt-4"
            >
              {loading
                ? "Patientez…"
                : mode === "register"
                  ? "Créer mon compte"
                  : "Se connecter"}
            </button>
          </form>
          <p className="mt-8 text-sm text-[#5C5754] text-center">
            {mode === "register" ? "Déjà un compte ?" : "Pas encore de compte ?"}{" "}
            <button
              data-testid="auth-switch-mode"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="text-[#C86B5E] hover:text-[#B35A4E] font-medium"
            >
              {mode === "register" ? "Se connecter" : "Créer un compte"}
            </button>
          </p>
          <p className="mt-6 text-center">
            <Link to="/" className="text-xs text-[#8A8480] hover:text-[#C86B5E] tracking-wider">
              ← Retour à l'accueil
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
